import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Popover, List, Button, Typography, Empty, Spin } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import * as notificationService from '@/services/api/notification.service';
import type { Notification } from '@/services/api/notification.service';
import './NotificationBell.css';

const { Text } = Typography;
const POLL_INTERVAL = 30_000;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const NOTIF_SOUND_KEY = 'notification_sound_enabled';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const prevUnread = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const playSound = useCallback(() => {
    const enabled = localStorage.getItem(NOTIF_SOUND_KEY) !== 'false';
    if (!enabled) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(
          'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVoGAACAgICAgICAgICAgICAgICAgICAgIB/f39/f39/f35+fn19fXx8fHt7enp5eXh4d3d2dnV1dHRzcnJxcXBwb25ubW1sbGtramppZ2dmZmVlZGRkY2NjY2JiYmJiYmJiYmJiY2NjY2RkZGVlZmZnZ2hpaWprbGxtbW5vb3BxcXJyc3R1dnZ3eHl5ent7fH19fn5/f4CAgICBgYKCgoODhISFhYaGh4eIiIiJiYmKiouLi4yMjY2Njo6Oj4+QkJCRkZGRkpKSkpOTk5OTlJSUlJSUlJSUlJSUlJOTk5OTk5KSkpKSkZGRkZCQkJCPj4+Ojo6NjY2MjIyLi4uKioqJiYiIiIeHh4aGhYWEhISIiIiI'
        );
        audioRef.current.volume = 0.3;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch { /* ignore */ }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      const newUnread = data.unread ?? 0;
      if (newUnread > prevUnread.current && prevUnread.current >= 0) {
        playSound();
      }
      prevUnread.current = newUnread;
      setUnreadCount(newUnread);
    } catch { /* ignore */ }
  }, [playSound]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationService.listNotifications({ limit: 20, offset: 0 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread ?? 0);
      prevUnread.current = data.unread ?? 0;
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const handleRead = async (n: Notification) => {
    if (!n.is_read) {
      await notificationService.markAsRead(n.id);
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    if (n.reference_type === 'ticket' && n.reference_id) {
      navigate(`/tickets/${n.reference_id}`);
      setOpen(false);
    }
  };

  const handleMarkAll = async () => {
    await notificationService.markAllAsRead();
    setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnreadCount(0);
  };

  const content = (
    <div className="notif-bell-dropdown">
      <div className="notif-bell-header">
        <Text strong>Notifications</Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" icon={<CheckOutlined />} onClick={handleMarkAll}>
            Mark all read
          </Button>
        )}
      </div>
      {loading ? (
        <div className="notif-bell-loading"><Spin size="small" /></div>
      ) : notifications.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" />
      ) : (
        <List
          className="notif-bell-list"
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              className={`notif-bell-item ${item.is_read ? '' : 'notif-bell-unread'}`}
              onClick={() => handleRead(item)}
            >
              <div className="notif-bell-item-inner">
                <Text strong={!item.is_read} className="notif-bell-title">{item.title}</Text>
                {item.message && (
                  <Text type="secondary" className="notif-bell-message" ellipsis>
                    {item.message}
                  </Text>
                )}
                <Text type="secondary" className="notif-bell-time">{timeAgo(item.created_at)}</Text>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayClassName="notif-bell-popover"
    >
      <button className="notif-bell-btn" aria-label="Notifications">
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <BellOutlined className="notif-bell-icon" />
        </Badge>
      </button>
    </Popover>
  );
};

export default NotificationBell;
